import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';

const StyledHero = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)',
    padding: theme.spacing(8, 0),
    textAlign: 'center',
    color: 'white'
}));

const StyledSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6, 0)
}));

const InfoCard = styled(Card)(() => ({
    height: '100%',
    borderRadius: '18px',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.12)'
}));

const IconBadge = styled(Box)(({ theme }) => ({
    width: 60,
    height: 60,
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    color: theme.palette.primary.main
}));

export default function UserDataDeletion() {
    const steps = [
        'Send your deletion request from your registered email address to info@glamourradiance.com.',
        'Use the subject line: User Data Deletion Request.',
        'Include your full name, registered mobile number, and any account or order reference that helps us identify your profile.',
        'Our team will review the request, verify ownership when needed, and process eligible deletion or anonymization actions.'
    ];

    const outcomes = [
        'Account profile details associated with your request',
        'Contact and communication records linked to the registered account',
        'Order-related personal information, unless retention is required for legal, tax, fraud-prevention, or compliance reasons',
        'Marketing preferences and promotional communication history'
    ];

    const notes = [
        'Some records may be retained where required by law, payment regulations, taxation, dispute handling, or security obligations.',
        'If complete deletion is not legally possible, we will restrict processing and retain only the minimum required data.',
        'Processing timelines can vary based on verification and regulatory checks.'
    ];

    return (
        <Box>
            <StyledHero>
                <Container maxWidth="lg">
                    <DeleteOutlineIcon sx={{ fontSize: '4rem', mb: 2, opacity: 0.95 }} />
                    <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                        User Data Deletion
                    </Typography>
                    <Typography
                        variant="h5"
                        sx={{ maxWidth: 860, mx: 'auto', lineHeight: 1.6, fontWeight: 300, opacity: 0.95 }}
                    >
                        If you want us to delete personal data associated with your account, you can submit a request
                        using the process below. We review each request and complete deletion wherever legally permitted.
                    </Typography>
                </Container>
            </StyledHero>

            <StyledSection sx={{ py: 2, backgroundColor: 'grey.100' }}>
                <Container maxWidth="lg">
                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }}>
                        Last updated: May 8, 2026
                    </Typography>
                </Container>
            </StyledSection>

            <StyledSection>
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <InfoCard>
                                <CardContent sx={{ p: 4 }}>
                                    <IconBadge>
                                        <EmailOutlinedIcon fontSize="large" />
                                    </IconBadge>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                                        How to Request
                                    </Typography>
                                    <List sx={{ px: 0 }}>
                                        {steps.map((step) => (
                                            <ListItem key={step} sx={{ px: 0, alignItems: 'flex-start' }}>
                                                <ListItemIcon sx={{ minWidth: 30, mt: '2px' }}>
                                                    <TaskAltOutlinedIcon color="primary" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText primary={step} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </InfoCard>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InfoCard>
                                <CardContent sx={{ p: 4 }}>
                                    <IconBadge>
                                        <DescriptionOutlinedIcon fontSize="large" />
                                    </IconBadge>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                                        Data Covered
                                    </Typography>
                                    <List sx={{ px: 0 }}>
                                        {outcomes.map((item) => (
                                            <ListItem key={item} sx={{ px: 0, alignItems: 'flex-start' }}>
                                                <ListItemIcon sx={{ minWidth: 30, mt: '2px' }}>
                                                    <TaskAltOutlinedIcon color="primary" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText primary={item} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </InfoCard>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <InfoCard>
                                <CardContent sx={{ p: 4 }}>
                                    <IconBadge>
                                        <SecurityOutlinedIcon fontSize="large" />
                                    </IconBadge>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                                        Important Notes
                                    </Typography>
                                    <List sx={{ px: 0 }}>
                                        {notes.map((note) => (
                                            <ListItem key={note} sx={{ px: 0, alignItems: 'flex-start' }}>
                                                <ListItemIcon sx={{ minWidth: 30, mt: '2px' }}>
                                                    <HelpOutlineOutlinedIcon color="primary" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText primary={note} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </InfoCard>
                        </Grid>
                    </Grid>
                </Container>
            </StyledSection>

            <Divider sx={{ mx: 4 }} />

            <StyledSection sx={{ backgroundColor: 'grey.50' }}>
                <Container maxWidth="md">
                    <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', fontWeight: 600, color: 'primary.main' }}>
                        Contact for Deletion Requests
                    </Typography>
                    <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '1.05rem', lineHeight: 1.8 }}>
                        Email your request to <strong>info@glamourradiance.com</strong>. If additional verification is
                        needed, our team will contact you before processing the request.
                    </Typography>
                </Container>
            </StyledSection>
        </Box>
    );
}
